import React, { useEffect, useState } from 'react';
import { XIcon, HomeIcon, BookIcon, ActivityIcon, MessageSquareIcon, SettingsIcon, HeadphonesIcon, UserIcon, PanelLeftIcon, HeartIcon, HelpCircleIcon, HistoryIcon, BookmarkIcon, SparklesIcon, CloudIcon, BrainIcon, CheckCircleIcon, RefreshCwIcon } from 'lucide-react';
import { useUserData } from '../context/UserDataContext';
interface EdgeMenuProps {
  currentView: string;
  setCurrentView: (view: string) => void;
  onClose: () => void;
}
const EdgeMenu: React.FC<EdgeMenuProps> = ({
  currentView,
  setCurrentView,
  onClose
}) => {
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [menuExpanded, setMenuExpanded] = useState(false);
  const [hoverItem, setHoverItem] = useState<string | null>(null);
  // Use the shared context instead of local state
  const {
    userProgress
  } = useUserData();
  // Simulate a dynamic menu that adapts to user state
  useEffect(() => {
    // This would normally be based on actual user data
    setActiveSection('main');
  }, []);
  const menuSections = {
    main: [{
      id: 'home',
      label: 'Home',
      icon: <HomeIcon className="w-5 h-5" data-id="element-310" />,
      description: 'Jouw dagelijkse overzicht'
    }, {
      id: 'themes',
      label: "Thema's & Oefeningen",
      icon: <BookIcon className="w-5 h-5" data-id="element-311" />,
      description: 'Ontdek oefeningen voor jouw doelen',
      badge: '3 nieuw'
    }, {
      id: 'diary',
      label: 'Dagboek',
      icon: <MessageSquareIcon className="w-5 h-5" data-id="element-312" />,
      description: 'Reflecteer op je gedachten'
    }, {
      id: 'soundscapes',
      label: 'Soundscapes',
      icon: <HeadphonesIcon className="w-5 h-5" data-id="element-313" />,
      description: 'Rustgevende geluiden voor focus en ontspanning'
    }],
    personal: [{
      id: 'favorites',
      label: 'Favorieten',
      icon: <BookmarkIcon className="w-5 h-5" data-id="element-314" />,
      description: 'Je opgeslagen content'
    }, {
      id: 'history',
      label: 'Geschiedenis',
      icon: <HistoryIcon className="w-5 h-5" data-id="element-315" />,
      description: 'Je recente activiteiten'
    }],
    support: [{
      id: 'help',
      label: 'Hulp',
      icon: <HelpCircleIcon className="w-5 h-5" data-id="element-316" />,
      description: 'Krijg ondersteuning'
    }, {
      id: 'settings',
      label: 'Instellingen',
      icon: <SettingsIcon className="w-5 h-5" data-id="element-317" />,
      description: 'Personaliseer je ervaring'
    }]
  };
  const handleMenuItemClick = (viewId: string) => {
    setCurrentView(viewId);
    onClose();
    // For history item which doesn't have a dedicated screen yet
    if (viewId === 'history') {
      setTimeout(() => {
        alert('Geschiedenis functie komt binnenkort beschikbaar');
        setCurrentView('home');
      }, 100);
    }
  };
  const handleProfileClick = () => {
    setCurrentView('settings');
    onClose();
  };
  const renderMenuItem = (item: any) => {
    const isActive = currentView === item.id;
    const isHovered = hoverItem === item.id;
    return <li key={item.id} data-id="element-318">
        <button className={`w-full flex items-start p-4 rounded-xl transition-all duration-300 relative ${isActive ? 'bg-gray-100 border-2 border-gray-300' : isHovered ? 'bg-gray-50 border-2 border-gray-200' : 'hover:bg-gray-50 border-2 border-transparent'}`} onClick={() => handleMenuItemClick(item.id)} onMouseEnter={() => setHoverItem(item.id)} onMouseLeave={() => setHoverItem(null)} data-id="element-319">
          <span className={`mr-4 text-gray-700 ${isActive ? 'scale-110' : ''} transition-transform duration-300`} data-id="element-320">
            {item.icon}
          </span>
          <div className="text-left" data-id="element-321">
            <span className={`block text-gray-800 ${isActive ? 'font-medium' : ''}`} data-id="element-322">
              {item.label}
            </span>
            {(menuExpanded || isHovered || isActive) && <span className="text-xs text-gray-500 mt-1 block transition-opacity duration-300" data-id="element-323">
                {item.description}
              </span>}
          </div>
          {item.badge && <span className="absolute top-2 right-2 px-2 py-0.5 bg-gray-200 rounded-full text-xs text-gray-700" data-id="element-324">
              {item.badge}
            </span>}
        </button>
      </li>;
  };
  return <div className="p-6 h-full flex flex-col bg-white relative" data-id="element-325">
      {/* Ambient background pattern - would be animated in real implementation */}
      <div className="absolute inset-0 pointer-events-none opacity-5" data-id="element-326">
        <div className="absolute top-0 right-0 w-64 h-64 border-2 border-gray-300 rounded-full transform translate-x-1/3 -translate-y-1/3" data-id="element-327"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 border-2 border-gray-300 rounded-full transform -translate-x-1/3 translate-y-1/3" data-id="element-328"></div>
      </div>
      <div className="relative z-10" data-id="element-329">
        <div className="flex justify-between items-center mb-6" data-id="element-330">
          <div className="flex items-center" data-id="element-331">
            <div className="w-10 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center mr-3" data-id="element-332">
              <BrainIcon className="w-5 h-5 text-gray-700" data-id="element-333" />
            </div>
            <h2 className="text-2xl font-light text-gray-800" data-id="element-334">Mosy</h2>
          </div>
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 active:bg-gray-200 transition-colors duration-200 touch-manipulation" aria-label="Close menu" data-id="element-335">
            <XIcon className="w-5 h-5 text-gray-600" data-id="element-336" />
          </button>
        </div>
        {/* User progress summary */}
        <div className="mb-8 p-5 border-2 border-gray-300 rounded-xl bg-gray-50" data-id="element-337">
          <div className="flex items-start" data-id="element-338">
            <div className="mr-4" data-id="element-339">
              <div className="w-12 h-12 rounded-full border-2 border-gray-300 flex items-center justify-center bg-white" data-id="element-340">
                <SparklesIcon className="w-6 h-6 text-gray-700" data-id="element-341" />
              </div>
            </div>
            <div data-id="element-342">
              <h3 className="text-sm font-medium text-gray-800 mb-1" data-id="element-343">
                Je zit nu in week {userProgress.weekNumber} van je{' '}
                {userProgress.phase}
              </h3>
              <div className="flex items-center mb-2" data-id="element-344">
                <CheckCircleIcon className="w-4 h-4 text-gray-600 mr-1" data-id="element-345" />
                <span className="text-xs text-gray-600" data-id="element-346">
                  {userProgress.completedToday} activiteiten vandaag
                </span>
              </div>
              <div className="flex items-center" data-id="element-347">
                <RefreshCwIcon className="w-4 h-4 text-gray-600 mr-1" data-id="element-348" />
                <span className="text-xs text-gray-600" data-id="element-349">
                  {userProgress.streak} dagen streak
                </span>
              </div>
            </div>
          </div>
        </div>
        <button className="w-full mb-6 py-3 flex items-center justify-center border-2 border-gray-300 rounded-xl hover:bg-gray-50 transition-colors duration-200" onClick={() => setMenuExpanded(!menuExpanded)} data-id="element-350">
          <span className="text-sm text-gray-700" data-id="element-351">
            {menuExpanded ? 'Compacte weergave' : 'Uitgebreide weergave'}
          </span>
        </button>
        <nav className="flex-1 mb-6" data-id="element-352">
          <div className="space-y-6" data-id="element-353">
            <section data-id="element-354">
              <ul className="space-y-2" data-id="element-355">
                {menuSections.main.map(renderMenuItem)}
              </ul>
            </section>
            <section data-id="element-356">
              <h3 className="text-xs uppercase text-gray-500 font-medium mb-2 px-4" data-id="element-357">
                Persoonlijk
              </h3>
              <ul className="space-y-2" data-id="element-358">
                {menuSections.personal.map(renderMenuItem)}
              </ul>
            </section>
            <section data-id="element-359">
              <h3 className="text-xs uppercase text-gray-500 font-medium mb-2 px-4" data-id="element-360">
                Ondersteuning
              </h3>
              <ul className="space-y-2" data-id="element-361">
                {menuSections.support.map(renderMenuItem)}
              </ul>
            </section>
          </div>
        </nav>
        <div className="pt-4 border-t border-gray-200" data-id="element-362">
          <div className="flex items-center p-4 rounded-xl border-2 border-gray-300 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-all duration-300" onClick={handleProfileClick} data-id="element-363">
            <div className="w-12 h-12 rounded-full border-2 border-gray-300 bg-white flex items-center justify-center mr-4" data-id="element-364">
              <UserIcon className="w-6 h-6 text-gray-600" data-id="element-365" />
            </div>
            <div data-id="element-366">
              <p className="text-sm font-medium text-gray-800" data-id="element-367">Lisa</p>
              <p className="text-xs text-gray-500" data-id="element-368">Persoonlijke instellingen</p>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default EdgeMenu;